"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import Container from "@/components/ui/Container";
import SectionHeading from "@/components/ui/SectionHeading";
import JsonLd from "@/components/JsonLd";
import { faqSchema } from "@/lib/structured-data";

export interface FaqItem {
  q: string;
  a: string;
}

interface FaqAccordionProps {
  eyebrow?: string;
  title?: string | { text: string; highlight?: boolean }[];
  subtitle?: string;
  items: FaqItem[];
  /** Emit FAQPage JSON-LD inline. Default true. Set false if parent emits schema separately. */
  emitSchema?: boolean;
}

export default function FaqAccordion({
  eyebrow = "FAQs",
  title = [{ text: "Frequently " }, { text: "asked questions", highlight: true }],
  subtitle,
  items,
  emitSchema = true,
}: FaqAccordionProps) {
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  if (items.length === 0) return null;

  return (
    <section className="section-pad border-t border-line">
      <Container>
        <SectionHeading
          eyebrow={eyebrow}
          title={title}
          subtitle={subtitle}
          className="mb-12"
        />

        <div className="mx-auto max-w-3xl divide-y divide-line">
          {items.map((item, i) => {
            const isOpen = openIdx === i;
            return (
              <div key={item.q} className="py-5">
                <button
                  type="button"
                  onClick={() => setOpenIdx(isOpen ? null : i)}
                  className="flex w-full items-center justify-between gap-4 text-left"
                  aria-expanded={isOpen}
                >
                  <h3 className="text-base font-semibold text-text-primary sm:text-lg">
                    {item.q}
                  </h3>
                  <Plus
                    size={18}
                    aria-hidden
                    className={`shrink-0 text-brand-purple-light transition-transform duration-200 ${
                      isOpen ? "rotate-45" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <p className="mt-3 text-base leading-relaxed text-text-secondary">
                    {item.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </Container>

      {emitSchema && (
        <JsonLd
          data={faqSchema(
            items.map((item) => ({ question: item.q, answer: item.a })),
          )}
        />
      )}
    </section>
  );
}
